import { useEffect, useState, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { useWorkflowActionMeta } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Template } from 'dashboard/models/template';
import { WorkflowRunSimulateContext } from 'dashboard/models/workflow-run-simulated';
import TriggerFieldDefinitionPanel from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerFieldDefinitionPanel';
import {
  generateSimulatedTriggerFieldOverrides,
  sanitizeFields,
} from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/utils';
import Spacer from 'design-system/components/Spacer';
import { FieldSchema } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import { ApiEndpoint } from 'lib/interfaces/api';

interface TriggerOverrideInquiryEventProps {
  setSimulateContext: (arg: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  setTriggerFieldSchemas: (fieldSchemas: FieldSchema[]) => void;
  triggerFieldSchemas: FieldSchema[];
  templateOptions: { label: string; value: string }[];
  disabled: boolean;
  requiredFields: string[];
  referencedFields: string[];
  triggerFieldDefinitionPanelOpen: boolean;
  toggleTriggerFieldDefinitionPanel: () => void;
  closeTriggerFieldDefinitionPanel: () => void;
}

const TriggerOverrideInquiryEvent: VFC<TriggerOverrideInquiryEventProps> = ({
  setSimulateContext,
  simulateContext,
  setTriggerFieldSchemas,
  triggerFieldSchemas,
  templateOptions,
  disabled,
  requiredFields,
  referencedFields,
  triggerFieldDefinitionPanelOpen,
  toggleTriggerFieldDefinitionPanel,
  closeTriggerFieldDefinitionPanel,
}) => {
  const actionMeta = useWorkflowActionMeta();
  const [currentTemplateId, setCurrentTemplateId] = useState<string>(
    (simulateContext?.trigger?.type === 'inquiry' &&
      simulateContext.trigger['inquiry-template-id']) ||
      '',
  );

  const handleSelectTemplate = (value: string) => {
    setCurrentTemplateId(value);
    if (value === '') {
      setSimulateContext({
        ...simulateContext,
        trigger: undefined,
      });
    } else {
      fetchFieldSchemasAndUpdateSimulateContext(value);
    }
  };

  // if currentTemplateId exists on simulateContext, fetch field schemas
  useEffect(() => {
    if (currentTemplateId) {
      fetchFieldSchemasAndUpdateSimulateContext(currentTemplateId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { loading, callback: fetchFieldSchemasAndUpdateSimulateContext } = useApiCallback(
    async (templateId: string) => {
      let fieldSchemas: FieldSchema[] = [];
      if (templateId?.startsWith('itmpl_')) {
        const templateVersionId = actionMeta['inquiry-templates'].find(
          (one) => one.token === templateId,
        )?.latestPublishedVersion;
        if (templateVersionId) {
          const { data } = await SessionClientDashboard.getResource<InquiryTemplateVersion>(
            ApiEndpoint.InquiryTemplateVersion,
            templateVersionId,
          );
          fieldSchemas = data.attributes['field-schema'];
        }
      } else if (templateId != null) {
        const { data } = await SessionClientDashboard.getResource<Template>(
          ApiEndpoint.Template,
          templateId,
        );
        fieldSchemas = data.attributes['v2-field-schemas'];
      }

      setSimulateContext({
        ...simulateContext,
        trigger: {
          'inquiry-template-id': templateId,
          overrides: generateSimulatedTriggerFieldOverrides(fieldSchemas, simulateContext),
          type: 'inquiry',
        },
      });
      setTriggerFieldSchemas(fieldSchemas);
    },
    [actionMeta, setSimulateContext, simulateContext],
  );

  return (
    <>
      <Spacer height={1} />
      <ParamLabel
        labelText='Inquiry Template'
        htmlFor='override-inquiry-create-inquiry__template-input'
      />
      <ReactSelect
        value={currentTemplateId}
        options={templateOptions}
        onChange={handleSelectTemplate}
        disabled={disabled || loading}
        id='override-inquiry-create-inquiry__template'
        portal
        isClearable
      />
      {simulateContext.trigger && triggerFieldSchemas.length > 0 && (
        <>
          <Spacer height={2} />
          {triggerFieldDefinitionPanelOpen && (
            <TriggerFieldDefinitionPanel
              simulateContext={simulateContext}
              setSimulateContext={setSimulateContext}
              closePanel={closeTriggerFieldDefinitionPanel}
              fieldSchemas={triggerFieldSchemas}
              disabled={disabled}
              triggerType='inquiry'
              requiredFields={requiredFields}
              referencedFields={referencedFields}
            />
          )}
          <FieldsList
            fields={
              simulateContext.trigger?.overrides
                ? sanitizeFields(simulateContext.trigger.overrides)
                : {}
            }
            fieldKey='inquiry-override-fields'
            toggleEditPanel={toggleTriggerFieldDefinitionPanel}
            tokensToDisplayValue={{}}
            editable={!disabled}
            errors={[]}
          />
        </>
      )}
    </>
  );
};

export default TriggerOverrideInquiryEvent;
